import type { MarkerKind } from '@anytracker/core'

import styles from './MarkerInput.module.css'
import type { ValueInputProps } from './types.js'

interface Props extends ValueInputProps {
  readonly kind: MarkerKind
}

/**
 * A yes with no no (T-1002).
 *
 * One button. Pressed means it happened; not pressed means there is no record,
 * which is what "it did not happen" looks like everywhere else in this app
 * (ADR-0005) — so there is deliberately no second control for the negative.
 *
 * **Two taps is the failure mode.** A marker exists because "it happened" is
 * not worth a form, so anything that asks for confirmation, or that lands in a
 * state needing a second press to commit, has removed the reason for the type.
 * One press records; pressing again clears.
 *
 * A toggle rather than a button whose label changes: `aria-pressed` is how a
 * screen reader is told this is a state and not an action, and a control that
 * relabels itself from "Record" to "Recorded" announces a *different button*
 * appearing rather than the same one changing.
 */
export function MarkerInput({
  kind,
  track,
  value,
  onChange,
  disabled = false,
  labelledBy,
  describedBy,
  variant = 'compact',
}: Props) {
  void kind
  const recorded = value === true

  return (
    <div className={`${styles['root']} ${variant === 'full' ? styles['full'] : ''}`}>
      <button
        type="button"
        aria-pressed={recorded}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        disabled={disabled}
        className={`${styles['button']} ${recorded ? styles['recorded'] : ''}`}
        // Pressing a recorded marker clears it. A one-tap control makes a
        // mis-tap the most likely mistake this screen can produce, and the
        // record is not the event — so it has to be undoable from here.
        onClick={() => {
          onChange(recorded ? null : true)
        }}
      >
        <span className={styles['mark']} aria-hidden="true">
          {recorded ? <Tick /> : <Plus />}
        </span>
        {/*
          The track's own name, because that is what happened. "Record it" would
          be the same word on every marker in the app, and the log flow's
          heading has already asked the question.
        */}
        <span className={styles['label']}>{recorded ? 'Recorded' : track.name}</span>
      </button>
    </div>
  )
}

function Tick() {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" aria-hidden="true">
      <path
        d="M5 13l4 4 10-10"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Plus() {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" aria-hidden="true">
      <path
        d="M12 5v14M5 12h14"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  )
}
